import React, { useState, useRef, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import ButtonBasic from '../../atoms/buttonBasic/view';
import './style.css';

const ImageCropperModal = ({ show, imageSrc, onCancel, onSave }) => {
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [startPan, setStartPan] = useState({ x: 0, y: 0 });
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    // Configuración del área de recorte (cuadrada/circular)
    const CROP_SIZE = 250;

    useEffect(() => {
        if (imageSrc) {
            const img = new Image();
            img.src = imageSrc;
            img.onload = () => {
                imageRef.current = img;
                // Centrar imagen inicialmente
                const aspect = img.width / img.height;
                let initialZoom = 1;
                if (img.width < img.height) {
                    initialZoom = CROP_SIZE / img.width;
                } else {
                    initialZoom = CROP_SIZE / img.height;
                }
                setZoom(initialZoom);
                draw();
            };
        }
    }, [imageSrc]);

    useEffect(() => {
        draw();
    }, [zoom, offset]);

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas || !imageRef.current) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Dibujar fondo oscuro
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Configurar área de recorte (circular para perfil)
        ctx.save();
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, CROP_SIZE / 2, 0, Math.PI * 2);
        ctx.clip();

        // Dibujar imagen transformada
        const img = imageRef.current;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        ctx.translate(centerX + offset.x, centerY + offset.y);
        ctx.scale(zoom, zoom);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        ctx.restore();

        // Dibujar borde del área de recorte
        ctx.beginPath();
        ctx.arc(canvas.width / 2, canvas.height / 2, CROP_SIZE / 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartPan({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setOffset({
                x: e.clientX - startPan.x,
                y: e.clientY - startPan.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const zoomSensitivity = 0.001;
        const newZoom = zoom - e.deltaY * zoomSensitivity;
        setZoom(Math.max(0.1, Math.min(newZoom, 5)));
    };

    const handleSave = () => {
        const canvas = document.createElement('canvas');
        canvas.width = CROP_SIZE;
        canvas.height = CROP_SIZE;
        const ctx = canvas.getContext('2d');

        if (imageRef.current) {
            const img = imageRef.current;

            // Calcular posición relativa al centro del recorte
            // La lógica de dibujo original usa: translate(center + offset) -> scale -> draw(-w/2, -h/2)
            // Queremos dibujar en un canvas de CROP_SIZE x CROP_SIZE
            // El centro del canvas original era (canvas.width/2, canvas.height/2)
            // El centro del nuevo canvas es (CROP_SIZE/2, CROP_SIZE/2)

            // Offset relativo al centro
            const drawX = (CROP_SIZE / 2) + offset.x;
            const drawY = (CROP_SIZE / 2) + offset.y;

            ctx.translate(drawX, drawY);
            ctx.scale(zoom, zoom);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);
        }

        canvas.toBlob((blob) => {
            onSave(blob);
        }, 'image/jpeg', 0.9);
    };

    return (
        <Modal
            show={show}
            onHide={onCancel}
            centered
            backdrop="static"
            dialogClassName="image-cropper-modal"
            backdropClassName="image-cropper-backdrop"
            style={{ zIndex: 1060 }} // Forzar z-index inline también por seguridad
        >
            <Modal.Header closeButton>
                <Modal.Title>Editar imagen de perfil</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column align-items-center">
                <div
                    className="canvas-container"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                >
                    <canvas
                        ref={canvasRef}
                        width={400}
                        height={400}
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    />
                </div>
                <div className="controls mt-3 w-100 px-4">
                    <Form.Label>Zoom</Form.Label>
                    <Form.Range
                        min={0.1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                    />
                </div>
                <p className="text-muted mt-2 small">
                    Arrastra para mover, usa la rueda o el slider para hacer zoom
                </p>
            </Modal.Body>
            <Modal.Footer>
                <ButtonBasic className="btn-secondary" onClick={onCancel} title="Cancelar" />
                <ButtonBasic onClick={handleSave} title="Guardar" />
            </Modal.Footer>
        </Modal>
    );
};

export default ImageCropperModal;
